import { Controller } from "@hotwired/stimulus"

// ui--work-view-state — the editable grid's saved-views + autosave controller
// (W6 Track C). Stacks on the grid surface root NEXT to the engine
// (`data-controller="ui--work-editable-table ui--work-grid-groups
// ui--work-view-state"` — the flow url-state stacking pattern): this element
// IS the surface root, so every `ui--work:*` event below bubbles from it and
// companion controllers resolve as same-element lookups.
//
// ── URL/hash ownership split (one writer per param — the flow
//    flow_url_state_controller.js ownership-table discipline) ──────────────
//
//   ?work_view=       THIS controller — the active saved-view id.
//                     Every write is history.replaceState (viewer state never
//                     spams history — pin 2).
//   ?present=         flow_url_state_controller.js (TAKEN)
//   #focus=           flow_url_state_controller.js (TAKEN, read-only at boot)
//   #view= / #beat=   flow_story_controller.js (TAKEN)
//   #route= / #lens=  flow_inspect_controller.js (TAKEN)
//   ?selectedIssue=   ui--work-issue-url-state (kit sibling — TAKEN)
//   ?scale=           ui--work-timeline (kit sibling — TAKEN)
//
// ── View state ─────────────────────────────────────────────────────────────
// One plain JSON Map, camelCase, JSON.stringify-safe, no DOM references:
//
//   { group:   { fieldKey: null | "<field key>", collapsed: {"<key>": true},
//                hideEmpty: false },                    // THIS wave's segment
//     columns: { … },                                 // RESERVED: W6 Track A /
//     sort: null }                                    // RESERVED: W6 Track A
//
// Segments are one-writer-per-segment (the URL table's sibling rule): the
// group segment belongs to ui--work-grid-groups (patched through
// patchState("group", …)); columns/sort are reserved segments their waves
// patch through the same seam — never written opaquely anywhere else.
//
// ── Autosave + lock/unlock ────────────────────────────────────────────────
// Unlocked view: mutations debounce-coalesce into ONE
// `ui--work:view-change {view_id, action: "autosave", state}` per quiescence.
// Locked view: mutations render as temporary changes (the amber highlight the
// view bar carries), emit NO autosave, and stay revertible to the saved
// state. Persistence is host-side — the kit only emits and exposes
// adoptView(id, state) for the host's POST response.
//
// ── Boot replay (fail-closed) ─────────────────────────────────────────────
// Boot reads the SSR-hydrated saved-views/state attrs and the `?work_view=`
// id, and applies ONLY when everything resolves; ONE 500ms retry covers a
// slow Stimulus scan (the flow_url_state boot pattern — a missing surface
// never loops forever), after which the controller stays inert and the
// default view is untouched. The element-level boot marker keeps a Turbo
// cache restore from double-applying state (D1: replay once per element;
// listeners re-bind on every connect).
//
// Events (bubbling CustomEvents from the surface root):
//   ui--work:view-change {view_id, action, state?, name?}
//       action: "select" | "autosave" | "save-as-new" | "revert"
//               | "lock" | "unlock"
//   ui--work:view-apply  {view_id, state}   // a full state was applied;
//                                           // the engine + segment writers
//                                           // consume it (the W5 seam)

const VIEW_PARAM = "work_view"
const BOOT_MARKER = "data-work-view-state-booted"
const ENGINE_RETRY_MS = 500
const APPLY_EVENT = "ui--work:view-apply"
const CHANGE_EVENT = "ui--work:view-change"

// The state document's shape; unknown segments are preserved verbatim so a
// future writer's segment survives normalization (additive segments only).
// The spread keeps unknown keys; the known segments are overlaid normalized
// so their shapes stay enforced.
function normalizeState(raw) {
  const source = raw && typeof raw === "object" ? raw : {}
  const group = source.group && typeof source.group === "object" ? source.group : {}
  return {
    ...source,
    group: {
      fieldKey: typeof group.fieldKey === "string" && group.fieldKey !== "" ? group.fieldKey : null,
      collapsed: group.collapsed && typeof group.collapsed === "object" ? { ...group.collapsed } : {},
      hideEmpty: group.hideEmpty === true,
    },
    columns: source.columns && typeof source.columns === "object" ? { ...source.columns } : {},
    sort: source.sort === undefined ? null : source.sort,
  }
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value === undefined ? null : value))
}

export default class extends Controller {
  static targets = ["viewOption", "lockToggle", "saveAsNew", "revertButton", "temporaryIndicator"]

  static values = {
    // Host-supplied saved views: [{id, name, locked, state}] (JSON).
    savedViews: String,
    // SSR echo of the active view id ("" = none — the host renders the attr
    // only when a view is active).
    activeView: String,
    // SSR-hydrated working state (JSON) — the boot baseline before replay.
    state: String,
    // Quiescence window for autosave coalescing (ms).
    autosaveDelay: { type: Number, default: 600 },
  }

  connect() {
    // Idempotent connect (D1): a Turbo cache restore re-runs connect on the
    // SAME element — teardown first, then re-bind listeners; the boot replay
    // itself runs once per element (marker below).
    this._teardown()
    this._savedViews = this._scanJson(this.savedViewsValue) || []
    this._state = normalizeState(this._scanJson(this.stateValue))
    this._activeViewId = this.activeViewValue || null
    this._dirty = false
    this._savedSnapshot = deepClone(this._state)

    this._booted = this.element.hasAttribute(BOOT_MARKER)
    this.element.setAttribute(BOOT_MARKER, "")

    if (!this._booted) this._boot()
    this._refreshSelector()
  }

  disconnect() {
    this._teardown()
  }

  // -- Public data API (the host glue island calls these; zero transport) ----

  // The current working state — a fresh JSON-safe deep copy every call.
  getState() {
    return deepClone(this._state)
  }

  // One segment writer's entry point (one-writer-per-segment, see the header):
  // deep-merges `partial` into `state[segment]`, then marks the surface dirty
  // — coalesced autosave when unlocked, the temporary-change highlight when
  // locked.
  patchState(segment, partial) {
    const current = this._state[segment]
    const merged =
      current && typeof current === "object" && !Array.isArray(current)
        ? { ...current, ...partial }
        : partial
    this._state = { ...this._state, [segment]: merged }
    this._markDirty()
  }

  // Select a saved view: applies its FULL state (full-def convergence — keys
  // the view does not carry leave the working state), writes the URL param
  // and emits `view-change {action: "select"}` unless silenced at boot.
  selectView(viewId, { emit = true } = {}) {
    const view = this._savedViews.find((def) => def.id === viewId)
    if (!view) return false
    this._activeViewId = view.id
    this._state = normalizeState(view.state)
    this._savedSnapshot = deepClone(this._state)
    this._dirty = false
    this._cancelAutosave()
    this._refreshTemporaryState()
    this._refreshSelector()
    this._writeViewParam(view.id)
    this._applyToSurface()
    if (emit) {
      this._emitChange({ action: "select", view_id: view.id, state: this.getState() })
    }
    return true
  }

  // The host's POST response lands here: register/replace the saved view and
  // activate it — it becomes the current view (aria-current in the selector)
  // and owns `?work_view=`.
  adoptView(viewId, state, { name = null, locked = false } = {}) {
    if (!viewId) return false
    const def = {
      id: viewId,
      name: name || viewId,
      locked: locked === true,
      state: normalizeState(state),
    }
    const existing = this._savedViews.findIndex((entry) => entry.id === viewId)
    if (existing >= 0) this._savedViews.splice(existing, 1, def)
    else this._savedViews.push(def)
    this._savedViews = [...this._savedViews]
    return this.selectView(viewId, { emit: false })
  }

  // Save-as-new: the kit emits the intent + full state; the host persists and
  // calls adoptView with the assigned id. `name` is null when the host owns
  // naming (the view bar's affordance path). Non-string first args (a Stimulus
  // action hands the Event) are treated as "host names it".
  saveAsNew(name = null) {
    if (typeof name !== "string") name = null
    this._emitChange({ action: "save-as-new", view_id: null, name, state: this.getState() })
  }

  // The view bar's option action: reads the view id off the clicked item and
  // selects it (selectView stays the programmatic API with its options arg).
  pickView(event) {
    const id = event.currentTarget ? event.currentTarget.dataset.viewId : null
    if (id) this.selectView(id)
  }

  // Revert: converge the working state to the active view's saved state
  // EXACTLY (a full-def replace, the flow replaceNode discipline — retracted
  // keys leave; a merge would strand them).
  revert() {
    if (!this._dirty) return false
    this._state = deepClone(this._savedSnapshot)
    this._dirty = false
    this._cancelAutosave()
    this._refreshTemporaryState()
    this._applyToSurface()
    this._emitChange({ action: "revert", view_id: this._activeViewId, state: this.getState() })
    return true
  }

  // Lock/unlock toggle: a locked view renders mutations as temporary changes
  // (no autosave); unlocking resumes coalesced autosave.
  toggleLock() {
    const view = this._savedViews.find((def) => def.id === this._activeViewId)
    if (!view) return false
    view.locked = !view.locked
    this._refreshTemporaryState()
    this._emitChange({
      action: view.locked ? "lock" : "unlock",
      view_id: view.id,
      state: this.getState(),
    })
    return true
  }

  // -- Boot replay (fail-closed) ---------------------------------------------

  _boot() {
    const paramId = this._readViewParam()
    const wanted = paramId || this._activeViewId
    if (wanted && this._savedViews.some((def) => def.id === wanted)) {
      this.selectView(wanted, { emit: false })
      return
    }
    if (!paramId) return

    // Unknown/unresolvable `?work_view=`: ONE retry (a slow Stimulus scan or
    // a late hydration may still produce the view), then fail closed — the
    // default view stays untouched and this controller stays inert.
    this._retryTimer = setTimeout(() => {
      this._retryTimer = null
      if (this._savedViews.some((def) => def.id === paramId)) {
        this.selectView(paramId, { emit: false })
      }
    }, ENGINE_RETRY_MS)
  }

  // -- Teardown (D1) ----------------------------------------------------------

  _teardown() {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer)
      this._retryTimer = null
    }
    this._cancelAutosave()
  }

  // -- Internals ----------------------------------------------------------------

  _markDirty() {
    this._dirty = true
    const locked = this._activeViewLocked()
    if (locked) {
      // Locked views never autosave — mutations stay temporary until the user
      // saves as new or reverts (the amber highlight pair lives in the view
      // bar markup; this controller only toggles it).
      this._refreshTemporaryState()
      return
    }
    this._cancelAutosave()
    this._autosaveTimer = setTimeout(() => {
      this._autosaveTimer = null
      if (!this._dirty) return
      this._dirty = false
      this._savedSnapshot = deepClone(this._state)
      this._refreshTemporaryState()
      this._emitChange({ action: "autosave", view_id: this._activeViewId, state: this.getState() })
    }, this.autosaveDelay)
  }

  _cancelAutosave() {
    if (this._autosaveTimer) {
      clearTimeout(this._autosaveTimer)
      this._autosaveTimer = null
    }
  }

  _activeViewLocked() {
    const view = this._savedViews.find((def) => def.id === this._activeViewId)
    return view ? view.locked === true : false
  }

  _refreshTemporaryState() {
    // Plural targets array: empty (not throwing) when the bar chrome is absent.
    const indicator = this.temporaryIndicatorTargets[0] ?? null
    if (indicator) indicator.hidden = !(this._activeViewLocked() && this._dirty)
  }

  // Selector + toggle chrome sync: aria-current on the active view option,
  // aria-checked parity for the radio group, and the lock button's pressed
  // label state. Idempotent — every apply path funnels through here.
  _refreshSelector() {
    this.viewOptionTargets.forEach((option) => {
      const active = option.dataset.viewId === this._activeViewId
      option.setAttribute("aria-checked", active ? "true" : "false")
      if (active) option.setAttribute("aria-current", "true")
      else option.removeAttribute("aria-current")
    })
    this.lockToggleTargets.forEach((toggle) => {
      toggle.setAttribute("aria-pressed", this._activeViewLocked() ? "true" : "false")
    })
    this._refreshTemporaryState()
  }

  // Full-state application: the group segment drives the groups controller
  // directly (same-element stacked controller); the whole state is ALSO
  // announced as `ui--work:view-apply` so the engine and the reserved-segment
  // writers (W5 / Track A) consume their parts — events are the API.
  _applyToSurface() {
    const groups = this._groupsController()
    if (groups && typeof groups.applyGroupState === "function") {
      groups.applyGroupState(deepClone(this._state.group))
    }
    this.element.dispatchEvent(
      new CustomEvent(APPLY_EVENT, {
        detail: { view_id: this._activeViewId, state: this.getState() },
        bubbles: true,
      }),
    )
  }

  _groupsController() {
    try {
      return this.application.getControllerForElementAndIdentifier(this.element, "ui--work-grid-groups")
    } catch {
      return null
    }
  }

  _emitChange(detail) {
    this.element.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail, bubbles: true }))
  }

  _readViewParam() {
    return new URLSearchParams(window.location.search).get(VIEW_PARAM)
  }

  // replaceState ONLY (pin 2 — never pushState): foreign params survive
  // verbatim; the hash is untouched.
  _writeViewParam(viewId) {
    const params = new URLSearchParams(window.location.search)
    if (viewId) params.set(VIEW_PARAM, viewId)
    else params.delete(VIEW_PARAM)
    const query = params.toString()
    const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    window.history.replaceState(window.history.state, "", url)
  }

  _scanJson(raw) {
    if (!raw || raw === "") return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
}
